import { useState } from "react"

import { toast } from "sonner"

import Icon from "@/components/Icon"
import PushPreview from "@/components/incentives/PushPreview"
import { driversText } from "@/components/incentives/pushText"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  APP_COMMS_MAX_LENGTHS,
  type Incentive,
  useIncentiveAudienceCount,
  useSendIncentivePush,
} from "@/hooks/useIncentives"

type Props = { incentive: Incentive; disabled: boolean }

export default function SendPushButton({ incentive, disabled }: Props) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")
  const audience = useIncentiveAudienceCount(incentive.id, open)
  const sendPush = useSendIncentivePush()
  const drivers = audience.data?.drivers

  // Each send starts from the saved App Comms, and Ops can reword it for the push.
  const openWith = (next: boolean) => {
    if (next) {
      setTitle(incentive.app_comms.title)
      setBody(incentive.app_comms.text)
    }
    setOpen(next)
  }

  const send = async () => {
    try {
      const push = await sendPush.mutateAsync({ id: incentive.id, title, body })
      toast.success(`Sending the push to ${driversText(push.driver_count)}`)
    } catch (error) {
      toast.error("The push wasn't sent", {
        description: error instanceof Error ? error.message : "Unknown error",
      })
    }
  }

  const audienceText = audience.error
    ? "The drivers in its audience couldn't be counted, so it can't be sent."
    : drivers === undefined
      ? "Counting the drivers in its audience..."
      : drivers === 0
        ? "No drivers are in its audience, so there's no one to send it to."
        : `It goes to the ${driversText(drivers)} in its audience, and opens the shifts page when tapped.`

  return (
    <AlertDialog open={open} onOpenChange={openWith}>
      <AlertDialogTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled || sendPush.isPending}>
          <Icon name="bell" className="h-4 w-4 text-current" />
          Send push notification
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Send this incentive as a push?</AlertDialogTitle>
          <AlertDialogDescription>{audienceText}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <Label htmlFor="push-title">Push title</Label>
          <Input
            id="push-title"
            value={title}
            maxLength={APP_COMMS_MAX_LENGTHS.title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="push-body">Push text</Label>
          <Textarea
            id="push-body"
            value={body}
            rows={3}
            maxLength={APP_COMMS_MAX_LENGTHS.text}
            onChange={(event) => setBody(event.target.value)}
          />
        </div>
        <PushPreview title={title} body={body} />
        <AlertDialogFooter>
          <AlertDialogCancel>Go back</AlertDialogCancel>
          <AlertDialogAction disabled={!drivers || !title.trim() || !body.trim()} onClick={send}>
            Send push notification
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
